import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { testimonials, faqData, timelineData } from '@/mocks/home';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotEl) formData.delete('phone_alt');

      const res = await fetch('https://readdy.ai/api/form/d9u0k978l97n3eas3f70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络异常，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
              <a href="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-6 leading-tight">
                关于<span className="text-primary-600">樱境物语</span>
              </h1>
              <p className="text-foreground-600 leading-relaxed text-base md:text-lg mb-4">
                樱境物语创立于2024年，是一支由热爱叙事游戏与日式美学的创作者组成的团队。我们致力于打造能够触动心灵、唤起共鸣的沉浸式叙事体验。
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                我们的核心理念是「以樱为媒，以情动人」。樱花象征着生命的短暂与绚烂，正如每一个动人的故事——虽稍纵即逝，却能在心中留下永恒的印记。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                从最初的几个人在咖啡馆里画下第一张概念草图，到如今拥有超过500万玩家的樱境世界，我们始终保持着对故事的热爱和对品质的追求。未来，我们将继续在这片樱花纷飞的土地上，书写更多温暖人心的物语。
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-12 text-center">
              发展<span className="text-primary-600">历程</span>
            </h2>
            <div className="max-w-3xl mx-auto">
              {timelineData.map((item, idx) => (
                <div key={idx} className="relative flex gap-6 pb-10 last:pb-0">
                  {idx < timelineData.length - 1 && (
                    <div className="absolute left-[19px] top-10 bottom-0 w-px bg-background-300"></div>
                  )}
                  <div className="relative z-10 w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <span className="text-xs font-bold">{idx + 1}</span>
                  </div>
                  <div className="pt-1">
                    <span className="text-sm font-medium text-accent-600">{item.year}</span>
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mt-1">{item.title}</h3>
                    <p className="text-foreground-500 text-sm mt-1">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-12 text-center">
              玩家<span className="text-primary-600">口碑</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div key={idx} className="p-6 rounded-2xl bg-background-100">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${i < t.rating ? 'text-accent-500 ri-star-fill' : 'text-background-300 ri-star-line'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-600">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-12 text-center">
              常见<span className="text-primary-600">问题</span>
            </h2>
            <div className="space-y-4">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 flex items-center justify-between gap-4 text-foreground-900 font-medium list-none cursor-pointer">
                    <span>{faq.question}</span>
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-300 group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-foreground-400"></i>
                    </span>
                  </summary>
                  <div className="px-6 pb-5 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 text-center">
              联系<span className="text-primary-600">我们</span>
            </h2>
            <p className="text-foreground-500 text-center mb-10">
              有任何问题或建议？欢迎通过以下表单与我们联系，我们将在1-2个工作日内回复。
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 rounded-2xl p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-2">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-2">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-2">留言</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言（500字以内）"
                ></textarea>
              </div>

              <div className="honeypot-wrap">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3.5 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-center text-secondary-700 bg-secondary-50 rounded-lg py-3 px-4">
                  留言已成功发送！感谢您的反馈，我们会尽快回复。
                </p>
              )}
              {formStatus === 'error' && formError && (
                <p className="text-sm text-center text-red-600 bg-red-50 rounded-lg py-3 px-4">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于樱境物语',
            description: '樱境物语是以樱花为主题的沉浸式叙事体验，了解我们的故事、团队与发展历程。',
            url: 'https://cdsmhs.cn/about',
            isPartOf: { '@type': 'WebSite', name: '樱境物语' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cdsmhs.cn/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://cdsmhs.cn/about' },
            ],
          }),
        }}
      />
    </>
  );
}